import type {
  DashboardAsset,
  DashboardAssetReadinessStatus,
  DashboardAssetType,
  DashboardProject,
} from "../types.js";

export interface DashboardAssetInventoryViewModel {
  assets: DashboardAsset[];
  countsByType: Record<DashboardAssetType, number>;
  countsByReadiness: Record<DashboardAssetReadinessStatus, number>;
  storageUsedLabel: string;
  totalAssetsLabel: string;
}

const cachedProjectsByPrincipal = new Map<string, DashboardProject[]>();
const cachedAssetInventoryByPrincipal = new Map<
  string,
  DashboardAssetInventoryViewModel
>();

function cloneProject(project: DashboardProject): DashboardProject {
  return {
    ...project,
    collaborators: [...project.collaborators],
  };
}

function cloneAsset(asset: DashboardAsset): DashboardAsset {
  return {
    ...asset,
    metadata: asset.metadata.map((entry) => ({ ...entry })),
  };
}

function cloneAssetInventory(
  inventory: DashboardAssetInventoryViewModel,
): DashboardAssetInventoryViewModel {
  return {
    assets: inventory.assets.map(cloneAsset),
    countsByType: { ...inventory.countsByType },
    countsByReadiness: { ...inventory.countsByReadiness },
    storageUsedLabel: inventory.storageUsedLabel,
    totalAssetsLabel: inventory.totalAssetsLabel,
  };
}

function parseSizeLabel(sizeLabel: string): number {
  const match = sizeLabel.trim().match(/^([\d.,]+)\s*(KB|MB|GB|TB|B)$/i);
  if (!match) {
    return 0;
  }

  const magnitude = Number.parseFloat(match[1].replace(/,/g, ""));
  if (!Number.isFinite(magnitude)) {
    return 0;
  }

  const unit = match[2].toUpperCase();
  const multipliers: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4,
  };

  return magnitude * (multipliers[unit] ?? 0);
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  const formatted = value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${formatted} ${units[exponent]}`;
}

export function cloneDashboardProjects(
  projects: ReadonlyArray<DashboardProject>,
): DashboardProject[] {
  return projects.map(cloneProject);
}

export function createEmptyDashboardAssetInventory(): DashboardAssetInventoryViewModel {
  return {
    assets: [],
    countsByType: { model: 0, image: 0, pdf: 0, video: 0, cad: 0 },
    countsByReadiness: { staged: 0, processing: 0, ready: 0, failed: 0 },
    storageUsedLabel: "0 B",
    totalAssetsLabel: "0 assets",
  };
}

export function buildDashboardAssetInventory(
  assets: ReadonlyArray<DashboardAsset>,
): DashboardAssetInventoryViewModel {
  const nextAssets = assets.map(cloneAsset);
  const countsByType = {
    model: 0,
    image: 0,
    pdf: 0,
    video: 0,
    cad: 0,
  } as DashboardAssetInventoryViewModel["countsByType"];
  const countsByReadiness = {
    staged: 0,
    processing: 0,
    ready: 0,
    failed: 0,
  } as DashboardAssetInventoryViewModel["countsByReadiness"];

  let totalBytes = 0;
  for (const asset of nextAssets) {
    countsByType[asset.type] += 1;
    countsByReadiness[asset.readiness] += 1;
    totalBytes += parseSizeLabel(asset.sizeLabel);
  }

  return {
    assets: nextAssets,
    countsByType,
    countsByReadiness,
    storageUsedLabel: formatBytes(totalBytes),
    totalAssetsLabel: `${nextAssets.length} asset${nextAssets.length === 1 ? "" : "s"}`,
  };
}

export function clearCachedDashboardProjects(principalKey: string): void {
  cachedProjectsByPrincipal.delete(principalKey);
}

export function getCachedDashboardProjects(
  principalKey: string,
): DashboardProject[] | null {
  const cachedProjects = cachedProjectsByPrincipal.get(principalKey);
  return cachedProjects ? cloneDashboardProjects(cachedProjects) : null;
}

export function setCachedDashboardProjects(
  principalKey: string,
  projects: ReadonlyArray<DashboardProject>,
): void {
  cachedProjectsByPrincipal.set(principalKey, cloneDashboardProjects(projects));
}

export function getCachedDashboardAssetInventory(
  principalKey: string,
): DashboardAssetInventoryViewModel | null {
  const cachedInventory = cachedAssetInventoryByPrincipal.get(principalKey);
  return cachedInventory ? cloneAssetInventory(cachedInventory) : null;
}

export function setCachedDashboardAssetInventory(
  principalKey: string,
  inventory: DashboardAssetInventoryViewModel,
): void {
  cachedAssetInventoryByPrincipal.set(principalKey, cloneAssetInventory(inventory));
}

export function resetDashboardPreviousDataCacheForTests(): void {
  cachedProjectsByPrincipal.clear();
  cachedAssetInventoryByPrincipal.clear();
}
